import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { auth } from '../../firebase/firebase.config';
import { handleGoogleSignIn } from '../../firebase/user.service';
import { signInWithGoogle } from '../../firebase/auth.service';

const Signup = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');

    const [showPassword, setShowPassword] = useState(false);
    const [errors, setErrors] = useState({});
    const [globalError, setGlobalError] = useState('');
    const [loading, setLoading] = useState(false);
    const [googleLoading, setGoogleLoading] = useState(false);
    const [showGoogleConfirmModal, setShowGoogleConfirmModal] = useState(false);

    const navigate = useNavigate();

    const validate = () => {
        const e = {};
        if (!email.trim()) e.email = 'Email is required.';
        if (!password) e.password = 'Password is required.';
        else if (password.length < 6) e.password = 'Password must be at least 6 characters.';
        if (!confirmPassword) e.confirmPassword = 'Please confirm your password.';
        else if (password !== confirmPassword) e.confirmPassword = 'Passwords do not match.';
        return e;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setGlobalError('');

        const fieldErrors = validate();
        if (Object.keys(fieldErrors).length > 0) {
            setErrors(fieldErrors);
            return;
        }
        setErrors({});
        setLoading(true);

        try {
            const normalizedEmail = email.trim().toLowerCase();

            console.info('[signup]', {
                event: 'signup_started',
                email: normalizedEmail,
                timestamp: new Date().toISOString(),
            });

            const credential = await createUserWithEmailAndPassword(auth, normalizedEmail, password);

            console.info('[signup]', {
                event: 'auth_created',
                uid: credential.user.uid,
                email: normalizedEmail,
                timestamp: new Date().toISOString(),
            });

            // No Firestore write — profile creation happens on /complete-profile
            navigate('/complete-profile');
        } catch (err) {
            console.error('Signup error:', err);
            const code = err.code || '';
            const msg =
                code === 'auth/email-already-in-use'
                    ? 'This email is already in use. Please log in instead.'
                    : code === 'auth/weak-password'
                        ? 'Password is too weak. Use a stronger password.'
                        : code === 'auth/invalid-email'
                            ? 'Invalid email address.'
                            : err.message || 'Signup failed. Please try again.';
            setGlobalError(msg);
        } finally {
            setLoading(false);
        }
    };

    const handleGoogleButtonClick = () => {
        setGlobalError('');
        setShowGoogleConfirmModal(true);
    };

    const handleCancelGoogleConfirm = () => {
        setShowGoogleConfirmModal(false);
    };

    const handleConfirmGoogleSignup = async () => {
        setGlobalError('');
        setGoogleLoading(true);

        try {
            console.info('[auth.singleProvider]', {
                flow: 'signup',
                event: 'google_login_confirmed',
                timestamp: new Date().toISOString(),
            });

            const result = await signInWithGoogle();
            const firebaseUser = result.user;

            console.info('[auth.singleProvider]', {
                flow: 'signup',
                event: 'google_auth_success',
                uid: firebaseUser.uid,
                email: firebaseUser.email || null,
                timestamp: new Date().toISOString(),
            });

            const { needsOnboarding } = await handleGoogleSignIn(firebaseUser);

            if (needsOnboarding) {
                navigate('/complete-profile');
                return;
            }

            localStorage.setItem('uid', firebaseUser.uid);
            navigate('/dashboard');
        } catch (err) {
            console.error('Google signup error:', err);
            const code = err.code || '';
            if (code === 'auth/popup-closed-by-user') {
                // user closed popup
            } else if (code === 'auth/cancelled-popup-request') {
                // duplicate popup
            } else {
                setGlobalError(err.message || 'Google sign-in failed. Please try again.');
            }
        } finally {
            setGoogleLoading(false);
            setShowGoogleConfirmModal(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 px-2 py-8">
            <form
                className="w-full max-w-md bg-white rounded-2xl shadow-lg p-8 md:p-10 flex flex-col gap-2"
                onSubmit={handleSubmit}
                noValidate
            >
                <h2 className="text-2xl md:text-3xl font-bold mb-1">Create Account</h2>
                <p className="text-gray-500 text-base mb-4">
                    Enter your email and password to get started
                </p>

                <label className="text-sm font-medium">
                    Email <span className="text-red-500">*</span>
                </label>
                <div className="flex items-center bg-gray-100 rounded-md px-3 mt-1">
                    <span className="text-gray-400 mr-2">📧</span>
                    <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="Enter your email"
                        className="w-full bg-transparent outline-none py-3 text-base"
                        autoComplete="email"
                        disabled={loading || googleLoading}
                    />
                </div>
                {errors.email && <p className="text-red-500 text-xs">{errors.email}</p>}

                <label className="text-sm font-medium mt-3">
                    Password <span className="text-red-500">*</span>
                </label>
                <div className="flex items-center bg-gray-100 rounded-md px-3 mt-1">
                    <span className="text-gray-400 mr-2">🔒</span>
                    <input
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Create a password (min. 6 chars)"
                        className="w-full bg-transparent outline-none py-3 text-base"
                        autoComplete="new-password"
                        disabled={loading || googleLoading}
                    />
                    <span
                        className="ml-2 text-gray-400 cursor-pointer select-none"
                        onClick={() => setShowPassword((prev) => !prev)}
                        title={showPassword ? 'Hide password' : 'Show password'}
                    >
                        {showPassword ? '🙈' : '👁️'}
                    </span>
                </div>
                {errors.password && <p className="text-red-500 text-xs">{errors.password}</p>}

                <label className="text-sm font-medium mt-3">
                    Confirm Password <span className="text-red-500">*</span>
                </label>
                <div className="flex items-center bg-gray-100 rounded-md px-3 mt-1">
                    <span className="text-gray-400 mr-2">🔒</span>
                    <input
                        type={showPassword ? 'text' : 'password'}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        placeholder="Confirm your password"
                        className="w-full bg-transparent outline-none py-3 text-base"
                        autoComplete="new-password"
                        disabled={loading || googleLoading}
                    />
                </div>
                {errors.confirmPassword && (
                    <p className="text-red-500 text-xs">{errors.confirmPassword}</p>
                )}

                {globalError && (
                    <div className="text-red-500 text-sm text-center mt-2 bg-red-50 rounded p-2">
                        {globalError}
                    </div>
                )}

                <button
                    className="w-full bg-black text-white rounded-md py-3 font-semibold text-lg mt-4 hover:bg-gray-900 transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
                    type="submit"
                    disabled={loading || googleLoading}
                >
                    {loading && (
                        <svg
                            className="animate-spin h-5 w-5 text-white"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                        >
                            <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                            />
                            <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8v8z"
                            />
                        </svg>
                    )}
                    {loading ? 'Creating account...' : 'Create Account'}
                </button>

                <div className="flex items-center gap-3 my-4">
                    <div className="flex-1 h-px bg-gray-200" />
                    <span className="text-sm text-gray-400">or</span>
                    <div className="flex-1 h-px bg-gray-200" />
                </div>

                <button
                    type="button"
                    onClick={handleGoogleButtonClick}
                    disabled={loading || googleLoading}
                    className="w-full flex items-center justify-center gap-3 border border-gray-300 rounded-md py-3 font-medium text-base hover:bg-gray-50 transition-colors disabled:opacity-60"
                >
                    {googleLoading ? (
                        <svg
                            className="animate-spin h-5 w-5 text-gray-500"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                        >
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                        </svg>
                    ) : (
                        <svg width="20" height="20" viewBox="0 0 48 48">
                            <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                            <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                            <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                            <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
                        </svg>
                    )}
                    {googleLoading ? 'Signing in...' : 'Continue with Google'}
                </button>

                <div className="text-center text-sm text-gray-600 mt-4">
                    Already have an account?{' '}
                    <Link to="/login" className="text-black font-semibold hover:underline">
                        Log in
                    </Link>
                </div>

                <div className="mt-4 text-xs text-gray-500 text-center">
                    By creating an account, you agree to the{' '}
                    <a href="#" className="underline">Terms</a>,{' '}
                    <a href="#" className="underline">Privacy Policy</a>, and{' '}
                    <a href="#" className="underline">Refund &amp; Cancellation</a> policies.
                </div>
            </form>

            {showGoogleConfirmModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
                    <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
                        <h3 className="text-xl font-bold">Continue with Google?</h3>
                        <p className="mt-3 text-sm text-gray-600">
                            If an account already exists with a different sign-in method, continuing will replace the previous provider with Google.
                            You may not be able to use the previous login method after this.
                        </p>
                        <div className="mt-6 flex justify-end gap-3">
                            <button
                                type="button"
                                onClick={handleCancelGoogleConfirm}
                                className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium hover:bg-gray-50"
                                disabled={googleLoading}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleConfirmGoogleSignup}
                                className="rounded-md bg-black px-4 py-2 text-sm font-semibold text-white hover:bg-gray-900 disabled:opacity-60"
                                disabled={googleLoading}
                            >
                                Continue with Google
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Signup;
